/**
 * Shared caption drawing.
 *
 * `drawCaption` renders one frame of captions onto any Canvas2D-compatible
 * context. The browser preview passes a real CanvasRenderingContext2D; the
 * export worker passes a node-canvas / skia-canvas context. Because both go
 * through this exact function, preview and export match.
 *
 * Intentionally avoids `ctx.filter` and `ctx.letterSpacing` (inconsistent
 * across browser vs node-canvas). Letter spacing is done by per-glyph advance;
 * blur is approximated as alpha attenuation so both renderers agree.
 */
import { contrastingStroke } from './style.js';
import { IDENTITY, activeWordTransform, composeTransforms, evalEntrance, evalExit, } from './animation.js';
import { unitMotionAt, idleMotionAt, STILL } from './hook-motion.js';
import { metalGradient, drawBlurred, ITALIC_SKEW } from './paint.js';
/**
 * Decide whether a word sits BEHIND the subject in depth mode. A per-word
 * `depthBehind` flag wins; otherwise the template's `depthLayers` policy
 * applies. Exported so the renderer can split the caption into two passes.
 */
export function wordIsBehind(ws, isActive, depthLayers) {
    if (typeof ws.word.depthBehind === 'boolean')
        return ws.word.depthBehind;
    switch (depthLayers) {
        case 'emphasized':
            return Boolean(ws.word.emphasized);
        case 'active':
            return isActive;
        default:
            return true; // 'all' (and undefined): the whole caption is behind.
    }
}
function transformToAlpha(base, t) {
    // blurPx attenuates alpha so both renderers agree without a blur filter.
    const blurAtten = t.blurPx > 0 ? Math.max(0.15, 1 - t.blurPx / 16) : 1;
    return base * t.opacity * blurAtten;
}
function roundRectPath(ctx, x, y, w, h, r) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
}
/** Draw a string glyph-by-glyph applying a fixed letter spacing (px). */
function drawSpacedText(ctx, text, centerX, baselineY, letterSpacing, measurer, stroke) {
    const glyphs = [...text];
    // Per-glyph spacing splits by code point, which SHATTERS complex-script
    // grapheme clusters (Devanagari consonant+matra, etc.) into dotted-circle
    // placeholders. For text with combining marks, always render the whole string
    // (letter spacing is a stylistic nicety not worth breaking shaping over).
    if (letterSpacing === 0 || /\p{M}/u.test(text)) {
        ctx.textAlign = 'center';
        if (stroke)
            ctx.strokeText(text, centerX, baselineY);
        else
            ctx.fillText(text, centerX, baselineY);
        return;
    }
    const font = ctx.font;
    const widths = glyphs.map((g) => measurer.measure(g, font));
    const total = widths.reduce((a, b) => a + b, 0) + letterSpacing * (glyphs.length - 1);
    let cursor = centerX - total / 2;
    ctx.textAlign = 'left';
    glyphs.forEach((g, i) => {
        if (stroke)
            ctx.strokeText(g, cursor, baselineY);
        else
            ctx.fillText(g, cursor, baselineY);
        cursor += widths[i] + letterSpacing;
    });
    ctx.textAlign = 'center';
}
function scaleFromLetterSpacing(style, layout) {
    return style.letterSpacingPx * layout.scale;
}
/**
 * Draw all caption backgrounds + words for the current state onto `ctx`.
 * `nowMs` is the current playback time; used to evaluate segment entrance/exit
 * (already reflected in state) and per-active-word motion.
 */
export function drawCaption(ctx, draw) {
    const { style, layout, state } = draw;
    if (!state.segment || state.words.length === 0)
        return;
    // Group-level entrance/exit transform.
    const entranceLocal = state.entranceProgress * style.entrance.durationMs + style.entrance.delayMs;
    const exitLocal = state.exitProgress * style.exit.durationMs + style.exit.delayMs;
    const entranceT = evalEntrance(style.entrance, entranceLocal);
    const exitT = state.exitProgress > 0 ? evalExit(style.exit, exitLocal) : IDENTITY;
    const groupT = composeTransforms(entranceT, exitT);
    ctx.save();
    // Apply group transform around the caption box center.
    const cx = layout.box.x + layout.box.width / 2;
    const cy = layout.box.y + layout.box.height / 2;
    ctx.translate(cx + groupT.translateXPx, cy + groupT.translateYPx);
    ctx.scale(groupT.scale, groupT.scale);
    ctx.translate(-cx, -cy);
    const groupAlpha = transformToAlpha(1, groupT);
    const bg = style.background;
    const letterSpacing = scaleFromLetterSpacing(style, layout);
    // Backgrounds.
    if (bg.mode !== 'none' && bg.color) {
        const padX = bg.paddingXPx * layout.scale;
        const padY = bg.paddingYPx * layout.scale;
        const radius = bg.radiusPx * layout.scale;
        ctx.globalAlpha = groupAlpha;
        ctx.fillStyle = bg.color;
        if (bg.mode === 'block') {
            roundRectPath(ctx, layout.box.x - padX, layout.box.y - padY, layout.box.width + padX * 2, layout.box.height + padY * 2, radius);
            ctx.fill();
        }
        else if (bg.mode === 'perLine') {
            for (const line of layout.lines) {
                if (line.length === 0)
                    continue;
                const first = line[0];
                const last = line[line.length - 1];
                const left = first.cx - first.width / 2;
                const right = last.cx + last.width / 2;
                const top = first.baselineY - layout.lineHeightPx * 0.72;
                roundRectPath(ctx, left - padX, top - padY, right - left + padX * 2, layout.lineHeightPx + padY * 2, radius);
                ctx.fill();
            }
        }
        else if (bg.mode === 'perWord' || bg.mode === 'activeWord') {
            for (const line of layout.lines) {
                for (const pw of line) {
                    const isActive = pw.index === state.words[state.activeWordIndex]?.index;
                    if (bg.mode === 'activeWord' && !isActive)
                        continue;
                    const wsBg = state.words.find((w) => w.index === pw.index);
                    // Word-reveal: a not-yet-spoken word (and its box) is hidden.
                    if (style.revealMode === 'word' && wsBg?.phase === 'future')
                        continue;
                    if (draw.layerFilter && wsBg && !draw.layerFilter(wsBg, isActive))
                        continue;
                    const fill = bg.mode === 'activeWord' && style.activeWordHighlight !== false
                        ? (style.activeWord.highlightColor ?? bg.color)
                        : bg.color;
                    if (!fill)
                        continue;
                    ctx.fillStyle = fill;
                    const top = pw.baselineY - layout.lineHeightPx * 0.72;
                    roundRectPath(ctx, pw.cx - pw.width / 2 - padX, top - padY, pw.width + padX * 2, layout.lineHeightPx + padY * 2, radius);
                    ctx.fill();
                }
            }
        }
    }
    // Words.
    const activeIndex = state.words[state.activeWordIndex]?.index ?? -1;
    for (const line of layout.lines) {
        for (const pw of line) {
            const ws = state.words.find((w) => w.index === pw.index);
            if (!ws)
                continue;
            // Word-reveal: hide words that haven't been spoken yet.
            if (style.revealMode === 'word' && ws.phase === 'future')
                continue;
            if (draw.layerFilter && !draw.layerFilter(ws, pw.index === activeIndex))
                continue;
            drawWord(ctx, draw, pw, ws, activeIndex, groupAlpha, letterSpacing);
        }
    }
    ctx.restore();
}
/** Motion the word's own look asks for (entrance when spoken + hold). */
function wordMotionState(pw, ws, state, layout) {
    const spec = pw.look.motion;
    if (!spec || !state.segment)
        return null;
    const motion = {
        in: spec.in,
        out: { kind: 'none', durationMs: 0, distance: 0, blur: 0, easing: 'linear' },
        split: 'block',
        staggerMs: 0,
        idle: spec.hold,
        idleAmount: spec.holdAmount,
    };
    // The word's window: from when it is spoken until well past the caption (the
    // caption's own exit animation takes it off screen).
    const win = { startMs: Math.min(state.segment.startMs, ws.word.startMs), endMs: state.segment.endMs + 600_000 };
    const frame = { width: layout.frame.width, height: layout.frame.height, fontPx: pw.fontPx };
    const st = spec.in.kind === 'none'
        ? { ...STILL } // hold-only: no entrance, the word shows as normal
        : unitMotionAt(motion, win, ws.word.startMs, state.nowMs, frame);
    const idle = spec.hold !== 'none' && state.nowMs >= ws.word.startMs
        ? idleMotionAt(motion, { startMs: ws.word.startMs, endMs: Math.max(ws.word.startMs + 1, state.segment.endMs) }, state.nowMs, layout.frame)
        : { tx: 0, ty: 0, scale: 1, rotateDeg: 0 };
    return { st, idle };
}
function drawWord(ctx, draw, pw, ws, activeIndex, groupAlpha, letterSpacingBase) {
    const { style, layout, measurer, state } = draw;
    const look = pw.look;
    const isActive = pw.index === activeIndex;
    // The layout resolved this word's font (family / weight / row size).
    const font = pw.cssFont;
    const rowK = look.rowSize;
    let letterSpacing = letterSpacingBase * rowK;
    // A new-line emphasis (key word on its own bigger line) is not animated by the
    // active-word pop or the reveal entrance - its size + line position carry the
    // emphasis. (An explicit key-word MOTION still plays.)
    const staticEmphasis = Boolean(ws.word.emphasized && style.emphasis.newLine);
    // Per-word scale (active-word pop / emphasis).
    let targetScale = 1;
    if (staticEmphasis) {
        targetScale = style.emphasis.scale; // fixed bigger size, independent of active state
    }
    else {
        if (isActive && style.activeWordHighlight !== false)
            targetScale *= style.activeWord.scale;
        if (ws.word.emphasized)
            targetScale *= style.emphasis.scale;
    }
    targetScale *= ws.word.sizeScale ?? 1;
    // Motion is independent of the colour/size highlight: a word can still wiggle/
    // jelly/pop with highlighting off, and can hold still with highlighting on.
    const own = wordMotionState(pw, ws, state, layout);
    const animateActive = !own && isActive && !staticEmphasis && style.activeWordMotion !== false;
    const activeT = animateActive
        ? activeWordTransform(style.activeWord.animation, ws.activeProgress, targetScale, state.nowMs)
        : { ...IDENTITY, scale: targetScale };
    // Per-word reveal: as a word is spoken it animates in (pop/slide/fade) from
    // its own start time, composed with any active-word motion. Skipped for a
    // static emphasis word, and for a word with its OWN motion (which replaces it).
    let wt = activeT;
    if (style.revealMode === 'word' && !staticEmphasis && !own) {
        const spec = style.wordEntrance ?? style.activeWord.animation;
        wt = composeTransforms(evalEntrance(spec, state.nowMs - ws.word.startMs), activeT);
    }
    const mo = own?.st;
    const idle = own?.idle;
    const motionAlpha = mo ? mo.opacity : 1;
    if (motionAlpha <= 0.005)
        return;
    const c = ctx;
    ctx.save();
    ctx.font = font;
    ctx.textBaseline = 'alphabetic';
    ctx.globalAlpha = groupAlpha * transformToAlpha(1, wt) * motionAlpha;
    // Mask reveal: the word slides out of its own line band (clipped).
    let maskDy = 0;
    if (mo?.masked) {
        const top = pw.baselineY - pw.fontPx * targetScale * 0.95;
        const boxH = pw.fontPx * targetScale * 1.25;
        ctx.beginPath();
        ctx.rect(pw.cx - layout.frame.width, top, layout.frame.width * 2, boxH);
        ctx.clip();
        maskDy = mo.maskOffset * boxH;
    }
    // Translate for reveal slide + own motion, then scale around the word center
    // so neighbours don't move.
    const tx = wt.translateXPx * layout.scale + (mo?.tx ?? 0) + (idle?.tx ?? 0);
    const ty = wt.translateYPx * layout.scale + (mo?.ty ?? 0) + (idle?.ty ?? 0) + maskDy;
    const rot = wt.rotateDeg + (mo?.rotateDeg ?? 0) + (idle?.rotateDeg ?? 0);
    const motionScale = (mo?.scale ?? 1) * (idle?.scale ?? 1);
    ctx.translate(pw.cx + tx, pw.baselineY + ty);
    if (rot)
        ctx.rotate((rot * Math.PI) / 180);
    // Pill / marker box behind the word (the emphasis pill, or the word's own).
    // Drawn inside the word's motion so it moves + fades with it.
    const pillColor = look.highlightColor ??
        (ws.word.emphasized && style.emphasis.highlightColor && !style.emphasis.newLine ? style.emphasis.highlightColor : null);
    if (pillColor) {
        const fontPx = pw.fontPx;
        const emScale = ws.word.emphasized ? style.emphasis.scale || 1 : 1;
        const ascent = fontPx * 0.8 * emScale; // above baseline - covers caps + Devanagari matras
        const descent = fontPx * 0.24 * emScale; // below baseline
        const padY = fontPx * 0.13;
        const height = ascent + descent + padY * 2;
        const radius = Math.min(height * 0.34, fontPx * 0.42);
        ctx.save();
        if (motionScale !== 1)
            ctx.scale(motionScale, motionScale);
        ctx.fillStyle = pillColor;
        roundRectPath(ctx, -pw.width / 2, -ascent - padY, pw.width, height, radius);
        ctx.fill();
        ctx.restore();
    }
    const sc = wt.scale * motionScale;
    ctx.scale(sc, sc * wt.scaleYMul);
    ctx.translate(-pw.cx, -pw.baselineY);
    if (look.italic && c.transform) {
        ctx.translate(pw.cx, pw.baselineY);
        c.transform(1, 0, ITALIC_SKEW, 1, 0, 0);
        ctx.translate(-pw.cx, -pw.baselineY);
    }
    if (mo?.trackEm)
        letterSpacing += mo.trackEm * pw.fontPx;
    const text = displayTextFromWord(ws, style);
    // Mid blur-in/out: a soft blurred copy stands in for the crisp word.
    if (mo && mo.blurPx > 0.6) {
        drawBlurred(c, (dx, dy) => drawSpacedText(ctx, text, pw.cx + dx, pw.baselineY + dy, letterSpacing, measurer, false), wordColor(style, ws, isActive, look.rowColor), mo.blurPx, layout.frame.width);
        ctx.restore();
        return;
    }
    // 3D extrude body - a stack of offset copies drawn deepest-first (no shadow),
    // giving the word real depth before the face is drawn on top.
    if (style.extrude && style.extrude.depthPx > 0) {
        ctx.shadowColor = 'rgba(0,0,0,0)';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.fillStyle = style.extrude.color;
        const steps = Math.round(style.extrude.depthPx);
        const dx = style.extrude.dxPx * layout.scale;
        const dy = style.extrude.dyPx * layout.scale;
        for (let d = steps; d >= 1; d--) {
            drawSpacedText(ctx, text, pw.cx + dx * d, pw.baselineY + dy * d, letterSpacing, measurer, false);
        }
    }
    const faceFill = resolveFill(ctx, style, ws, isActive, pw, layout);
    // Glow (the key word's / this word's own), under everything else.
    if (look.glow) {
        ctx.save();
        ctx.shadowColor = look.glow.color;
        ctx.shadowBlur = Math.max(1, look.glow.blurPx * layout.scale);
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.fillStyle = faceFill;
        drawSpacedText(ctx, text, pw.cx, pw.baselineY, letterSpacing, measurer, false);
        drawSpacedText(ctx, text, pw.cx, pw.baselineY, letterSpacing, measurer, false);
        ctx.restore();
    }
    // Shadow.
    if (style.shadow && !look.glow) {
        ctx.shadowColor = style.shadow.color;
        ctx.shadowBlur = style.shadow.blurPx * layout.scale;
        ctx.shadowOffsetX = style.shadow.offsetXPx * layout.scale;
        ctx.shadowOffsetY = style.shadow.offsetYPx * layout.scale;
    }
    else {
        ctx.shadowColor = 'rgba(0,0,0,0)';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
    }
    // Stroke (outline) underneath fill - the word's resolved outline.
    if (look.stroke && look.stroke.widthPx > 0) {
        ctx.lineJoin = 'round';
        ctx.lineWidth = look.stroke.widthPx * layout.scale * rowK;
        // Don't let the outline blend into the fill (e.g. a white active word inside
        // a white outline) - swap to a contrasting outline when they're too close.
        ctx.strokeStyle = contrastingStroke(look.stroke.color, wordColor(style, ws, isActive, look.rowColor));
        drawSpacedText(ctx, text, pw.cx, pw.baselineY, letterSpacing, measurer, true);
        // Reset shadow after stroke so it isn't doubled on fill.
        ctx.shadowColor = 'rgba(0,0,0,0)';
        ctx.shadowBlur = 0;
    }
    if (style.activeWord.karaoke && style.activeWordHighlight !== false && (isActive || ws.phase === 'active')) {
        // Draw base color fully, then active color clipped to activeProgress width.
        ctx.fillStyle = wordColor(style, ws, false, look.rowColor);
        drawSpacedText(ctx, text, pw.cx, pw.baselineY, letterSpacing, measurer, false);
        ctx.save();
        const left = pw.cx - pw.width / 2;
        const fillW = pw.width * ws.activeProgress;
        ctx.beginPath();
        ctx.rect(left, pw.baselineY - layout.lineHeightPx, fillW, layout.lineHeightPx * 1.4);
        ctx.clip();
        ctx.fillStyle = style.activeWord.color;
        drawSpacedText(ctx, text, pw.cx, pw.baselineY, letterSpacing, measurer, false);
        ctx.restore();
    }
    else if (style.activeWord.karaoke && style.activeWordHighlight !== false && ws.phase === 'past') {
        ctx.fillStyle = style.activeWord.color;
        drawSpacedText(ctx, text, pw.cx, pw.baselineY, letterSpacing, measurer, false);
    }
    else {
        ctx.fillStyle = faceFill;
        drawSpacedText(ctx, text, pw.cx, pw.baselineY, letterSpacing, measurer, false);
    }
    ctx.restore();
}
/** Face fill: a linear gradient when the style defines one (base words only),
 *  otherwise the resolved solid color. */
function resolveFill(ctx, style, ws, isActive, pw, layout) {
    const look = pw.look;
    // The word's own fill (per-word style / key-word look): metal or gradient.
    if (look.fill === 'metal') {
        return metalGradient(ctx, wordColor(style, ws, isActive, look.rowColor), pw.baselineY - pw.fontPx * 0.78, pw.baselineY + pw.fontPx * 0.04);
    }
    if (look.fill === 'gradient' && look.gradient) {
        const g = look.gradient;
        const h = pw.fontPx;
        const midY = pw.baselineY - h * 0.36;
        const a = ((g.angleDeg % 360) * Math.PI) / 180;
        const grad = ctx.createLinearGradient(pw.cx - (Math.cos(a) * pw.width) / 2, midY - Math.sin(a) * h * 0.45, pw.cx + (Math.cos(a) * pw.width) / 2, midY + Math.sin(a) * h * 0.45);
        grad.addColorStop(0, g.from);
        grad.addColorStop(1, g.to);
        return grad;
    }
    if (style.gradient && !isActive && !ws.word.emphasized && !ws.word.colorOverride) {
        const h = layout.lineHeightPx;
        const cx = pw.cx;
        const midY = pw.baselineY - h * 0.3;
        const halfW = pw.width / 2;
        const halfH = h * 0.45;
        const a = ((style.gradient.angleDeg % 360) * Math.PI) / 180;
        const grad = ctx.createLinearGradient(cx - Math.cos(a) * halfW, midY - Math.sin(a) * halfH, cx + Math.cos(a) * halfW, midY + Math.sin(a) * halfH);
        grad.addColorStop(0, style.gradient.from);
        grad.addColorStop(1, style.gradient.to);
        return grad;
    }
    return wordColor(style, ws, isActive, look.rowColor);
}
function displayTextFromWord(ws, style) {
    const t = ws.word.text;
    switch (style.textTransform) {
        case 'uppercase':
            return t.toUpperCase();
        case 'lowercase':
            return t.toLowerCase();
        case 'capitalize':
            return t.replace(/(^|\s)(\p{L})/gu, (_m, s, c) => s + c.toUpperCase());
        default:
            return t;
    }
}
function wordColor(style, ws, isActive, rowColor) {
    if (ws.word.colorOverride)
        return ws.word.colorOverride;
    if (isActive && style.activeWordHighlight !== false)
        return style.activeWord.color;
    // The emphasized/key word takes its emphasis color when one is set (works for
    // both inline and new-line emphasis). A template that wants the key word to
    // keep the body color simply sets emphasis.color to that same color.
    if (ws.word.emphasized && style.emphasis.color)
        return style.emphasis.color;
    if (ws.phase === 'past' && style.spokenColor)
        return style.spokenColor;
    // A multi-line caption row with its own colour.
    if (rowColor)
        return rowColor;
    // Palette cycling: base words take a color by their position in the segment.
    if (style.palette && style.palette.length > 0) {
        return style.palette[((ws.index % style.palette.length) + style.palette.length) % style.palette.length];
    }
    return style.color;
}
//# sourceMappingURL=draw.js.map